import {
    DownloadIntent,
    Geometry,
    getBounds,
    getProgramId,
    OSKGeoJson,
    OSKScene,
    SceneFromStacItem,
    Sensor,
    StacAPI,
} from 'oskcore';
import { AppDispatch, RootState } from '../../store';
import { filter, flatMap } from 'lodash';
import { FootprintEntry, setFootprints } from './map';
import { add } from 'date-fns';
import { createSelector } from '@reduxjs/toolkit';
import { bucketByDateNoGaps } from '~/molecules/map/TimelineSlider/bucket';
import { StacItem } from 'stac-ts';
import L from 'leaflet';
import { enqueueFilesById } from './cart';

export type TimelineMode = 'daily' | 'weekly' | 'monthly' | 'yearly';

export type NullableDate = Date | null;

/**
 * Manual - Set by the user
 * Auto - Generated by code
 */
export type AoiType = 'manual' | 'auto' | 'viewport';

const SET_SEARCHING = 'SET_SEARCHING';
export function setSearching(isSearching: boolean) {
    return {
        type: SET_SEARCHING,
        payload: {
            isSearching,
        },
    };
}

const SET_ROI = 'SET_ROI';
export function setRoi(geoJson?: OSKGeoJson, aoiName?: string, aoiType?: AoiType) {
    return {
        type: SET_ROI,
        payload: {
            geoJson,
            aoiName,
            aoiType,
        },
    };
}

const UPDATE_SEARCH_RESULT = 'UPDATE_SEARCH_RESULT';
export function updateSearchResults(result?: Array<OSKScene>, retainSceneIds?: string[]) {
    return {
        type: UPDATE_SEARCH_RESULT,
        payload: {
            result,
            retainSceneIds,
        },
    };
}

const UPDATE_API_RESULTS_LENGTH = 'UPDATE_API_RESULTS_LENGTH';
export function updateApiResultsLength(length: number) {
    return {
        type: UPDATE_API_RESULTS_LENGTH,
        payload: {
            length,
        },
    };
}

const INCLUDE_PLATFORM = 'INCLUDE_PLATFORM';
export function includePlatform(platform: number) {
    return {
        type: INCLUDE_PLATFORM,
        payload: {
            platform,
        },
    };
}

const EXCLUDE_PLATFORM = 'EXCLUDE_PLATFORM';
export function excludePlatform(platform: number) {
    return {
        type: EXCLUDE_PLATFORM,
        payload: {
            platform,
        },
    };
}

const SET_SEARCH_ERROR = 'SET_SEARCH_ERROR';
export function setSearchError(searchError: boolean) {
    return {
        type: SET_SEARCH_ERROR,
        payload: {
            searchError,
        },
    };
}

const SET_DATE_RANGE = 'SET_DATE_RANGE';
export function setDateRange(start: NullableDate, end: NullableDate) {
    return {
        type: SET_DATE_RANGE,
        payload: {
            start,
            end,
        },
    };
}

const SET_CLOUD_COVER_FILTER = 'SET_CLOUD_COVER_FILTER';
export function setCloudCoverFilter(cloudCover: number) {
    return {
        type: SET_CLOUD_COVER_FILTER,
        payload: { cloudCover },
    };
}

const SET_SUN_AZIMUTH_FILTER = 'SET_SUN_AZIMUTH_FILTER';
export function setSunAzimuthFilter(sunAzimuthRange: number[]) {
    return {
        type: SET_SUN_AZIMUTH_FILTER,
        payload: { sunAzimuthRange },
    };
}

const SET_SUN_ELEVATION_FILTER = 'SET_SUN_ELEVATION_FILTER';
export function setSunElevationFilter(sunElevationRange: number[]) {
    return {
        type: SET_SUN_ELEVATION_FILTER,
        payload: { sunElevationRange },
    };
}

const SET_OFF_NADIR_FILTER = 'SET_OFF_NADIR_FILTER';
export function setOffNadirFilter(offNadirRange: number[]) {
    return {
        type: SET_OFF_NADIR_FILTER,
        payload: { offNadirRange },
    };
}

const SET_VIEW_AZIMUTH_FILTER = 'SET_VIEW_AZIMUTH_FILTER';
export function setViewAzimuthFilter(viewAzimuthRange: number[]) {
    return {
        type: SET_VIEW_AZIMUTH_FILTER,
        payload: { viewAzimuthRange },
    };
}

const SET_IMAGING_MODE_FILTER = 'SET_IMAGING_MODE_FILTER';
export function setImagingModeFilter(imagingModes: string[]) {
    return {
        type: SET_IMAGING_MODE_FILTER,
        payload: { imagingModes },
    };
}

const SET_PURCHASED_ITEMS_FILTER = 'SET_PURCHASED_ITEMS_FILTER';
export function setPurchasedItemsFilter(purchasedItemsOnly: boolean) {
    return {
        type: SET_PURCHASED_ITEMS_FILTER,
        payload: { purchasedItemsOnly },
    };
}

const SET_TIMELINE_MODE = 'SET_TIMELINE_MODE';
export function setTimelineMode(mode: TimelineMode) {
    return {
        type: SET_TIMELINE_MODE,
        payload: {
            mode,
        },
    };
}

const SET_TIMELINE_DATE = 'SET_TIMELINE_DATE';
export function setTimelineDate(date: NullableDate) {
    return {
        type: SET_TIMELINE_DATE,
        payload: {
            date,
        },
    };
}

const TOGGLE_SEARCH_PANEL = 'TOGGLE_SEARCH_PANEL';
export function toggleSearchPanel() {
    return {
        type: TOGGLE_SEARCH_PANEL,
        payload: {},
    };
}

const SET_IS_SEARCH_DIRTY = 'SET_IS_SEARCH_DIRTY';
export function setIsSearchDirty(isSearchDirty: boolean) {
    return {
        type: SET_IS_SEARCH_DIRTY,
        payload: {
            isSearchDirty,
        },
    };
}

const SET_OVERLAY_MODE_FOR_TASK = 'SET_VIEW_MODE_FOR_TASK';
export function setOverlayModeForTask(id: string, mode: FootprintOverlayMode) {
    return {
        type: SET_OVERLAY_MODE_FOR_TASK,
        payload: {
            id,
            mode,
        },
    };
}

const CLEAR_OVERLAY_MODES = 'CLEAR_OVERLAY_MODES';
export function clearOverlayModes() {
    return {
        type: CLEAR_OVERLAY_MODES,
    };
}

const ADD_CLOUD_OVERLAY_MAP = 'ADD_CLOUD_OVERLAY_MAP';
export function addCloudOverlayMap(file_id: string, cloud_overlay: string) {
    return {
        type: ADD_CLOUD_OVERLAY_MAP,
        payload: {
            file_id,
            cloud_overlay,
        },
    };
}

const CLEAR_CLOUD_OVERLAY_MAP = 'CLEAR_CLOUD_OVERLAY_MAP';
export function clearCloudOverlayMap() {
    return { type: CLEAR_CLOUD_OVERLAY_MAP };
}

const SET_FILTER_DATES = 'SET_FILTER_DATES';
export function setSearchFilterDates(start?: Date, end?: Date) {
    return {
        type: SET_FILTER_DATES,
        payload: { start, end },
    };
}

const SET_DISPLAY_STAC_ITEM = 'SET_DISPLAY_STAC_ITEM';
export function setStacItemPropertiesDisplay(displayStacItem?: StacItem) {
    return {
        type: SET_DISPLAY_STAC_ITEM,
        payload: { displayStacItem },
    };
}

const SET_TIMELINE_SCRUBBER_VISIBLE = 'SET_TIMELINE_SCRUBBER_VISIBLE';
export function setTimelineScrubberVisible(timelineScrubberVisible: boolean) {
    return {
        type: SET_TIMELINE_SCRUBBER_VISIBLE,
        payload: { timelineScrubberVisible },
    };
}

/*
    Filter Methods
*/

function computeStartDate(startDate: NullableDate, timelineDate: NullableDate): Date | undefined {
    if (timelineDate === null) {
        return startDate ?? undefined;
    } else {
        return timelineDate;
    }
}

function computeEndDate(
    endDate: NullableDate,
    timelineDate: NullableDate,
    timelineMode: TimelineMode,
): Date | undefined {
    if (timelineDate === null) {
        return endDate ?? undefined;
    } else {
        switch (timelineMode) {
            case 'daily':
                return timelineDate;
            case 'weekly':
                return add(timelineDate, { weeks: 1 });
            case 'monthly':
                return add(timelineDate, { months: 1 });
            case 'yearly':
                return add(timelineDate, { years: 1 });
        }
    }
}

export function doSearchAsync() {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const {
            roi,
            roiName,
            startDate,
            endDate,
            platforms,
            timelineMode,
            timelineDate,
            cloudCover,
            sunAzimuthRange,
            sunElevationRange,
            offNadirRange,
            viewAzimuthRange,
            imagingModes,
            purchasedItemsOnly,
        } = getState().data.search;

        const programId = getProgramId();

        // Compute the time periods
        const start = computeStartDate(startDate, timelineDate);
        const end = computeEndDate(endDate, timelineDate, timelineMode);
        const aoiFilter: any =
            roi !== undefined && roi !== null && (roi as OSKGeoJson).features.length > 0
                ? JSON.stringify((roi as OSKGeoJson).toAPIGeometry())
                : undefined;

        // Compute the aoi stuff
        const aoiSearchParams: any = {};
        if (roiName) {
            aoiSearchParams['aoiName'] = roiName;
        } else {
            aoiSearchParams['aoi'] = aoiFilter;
        }

        dispatch(setSearching(true));
        dispatch(setSearchError(false));

        StacAPI.searchCatalog({
            capturedAfter: start,
            capturedBefore: end,
            sensor: platforms,
            ...(cloudCover < 100 && { cloudCoverMax: cloudCover }),
            aoi: roi,
            sunAzimuthRange,
            sunElevationRange,
            offNadirRange,
            viewAzimuthRange,
            imagingModes,
            ...(purchasedItemsOnly && { allowedPrograms: programId }),
        })
            .then((result) => {
                const { numberMatched } = result;
                const resultScenes: Array<OSKScene> = result.features.map((stacItem: StacItem) =>
                    SceneFromStacItem(stacItem),
                );

                // Extract footprints
                const footprints: Array<FootprintEntry> = flatMap(resultScenes, (result) => ({
                    fileId: result.id,
                    taskId: result.task_id ?? 'NA NA NA',
                    footprint: OSKGeoJson.fromAPIGeometry(result.geometry as Geometry),
                }));

                dispatch(
                    updateSearchResults(
                        resultScenes,
                        Object.values(getState().data.cart.enqueued as Record<string, DownloadIntent | undefined>).map(
                            (di) => di?.fileId ?? '',
                        ),
                    ),
                );
                dispatch(updateApiResultsLength(numberMatched));
                // Beam footprints over to map store
                dispatch(setFootprints(footprints));
                dispatch(setIsSearchDirty(false));
            })
            .catch((ex) => {
                console.error(ex);
                dispatch(setSearchError(true));
            })
            .finally(() => {
                dispatch(setSearching(false));
            });
    };
}

export function doSearchAsyncForTaskId(taskId: string, enqueueAll?: boolean) {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(setSearching(true));
        dispatch(setSearchError(false));
        StacAPI.searchCatalog({ taskIds: [taskId] })
            .then((result) => {
                const { numberMatched } = result;
                const resultScenes: Array<OSKScene> = result.features.map((stacItem: StacItem) =>
                    SceneFromStacItem(stacItem),
                );

                // Extract footprints
                const footprints: Array<FootprintEntry> = flatMap(resultScenes, (result) => ({
                    fileId: result.id,
                    taskId: result.task_id ?? 'NA NA NA',
                    footprint: OSKGeoJson.fromAPIGeometry(result.geometry as Geometry),
                }));

                dispatch(
                    updateSearchResults(
                        resultScenes,
                        Object.values(getState().data.cart.enqueued as Record<string, DownloadIntent | undefined>).map(
                            (di) => di?.fileId ?? '',
                        ),
                    ),
                );
                dispatch(updateApiResultsLength(numberMatched ?? 0));

                // Beam footprints over to map store
                dispatch(setFootprints(footprints));
                dispatch(setIsSearchDirty(false));

                if (enqueueAll) {
                    const ids = resultScenes.map((scene) => scene.id);
                    // @ts-ignore
                    dispatch(enqueueFilesById(ids ?? []));
                }

                // Calculate the footprint's bounding box and focus the map on it
                if (footprints) {
                    const fb = getBounds(
                        footprints.map((footprint) => OSKGeoJson.fromAPIGeometry(footprint.footprint.toAPIGeometry())),
                    ).map((n) => [n[0], n[1]]);

                    if (fb.length > 0) {
                        const bounds = new L.LatLngBounds(
                            new L.LatLng(fb[1][0], fb[1][1]),
                            new L.LatLng(fb[0][0], fb[0][1]),
                        );

                        const aoi = OSKGeoJson.fromLatLngBounds(bounds);
                        dispatch(setRoi(aoi, '', 'auto'));
                    }
                }
            })
            .catch((ex) => {
                console.error(ex);
                dispatch(setSearchError(true));
            })
            .finally(() => {
                dispatch(setSearching(false));
            });
    };
}

/* Reducer */
type SearchStateType = {
    roi?: OSKGeoJson;
    roiName?: string;
    roiType?: AoiType;
    isSearching: boolean;
    results?: Array<OSKScene>;
    resultMap: Record<string, OSKScene>;
    apiResultsLength?: number;
    fileIdToCollectMap?: Record<string, string>;
    /** An object containing collect to fileId mapping. The key is collect, the value is an array of fileIds */
    collectToFileIdList: Record<string, string[]>;
    platforms: Array<number>;
    startDate: NullableDate;
    endDate: NullableDate;
    timelineMode: TimelineMode;
    timelineDate: NullableDate;
    searchPanel: boolean;
    isSearchDirty?: boolean;
    cloudCover?: number;
    sunAzimuthRange?: number[]; // [low, high]
    sunElevationRange?: number[]; // [low, high]
    offNadirRange?: number[]; // [low, high]
    viewAzimuthRange?: number; // [low, high]
    imagingModes?: string[];
    purchasedItemsOnly?: boolean;
    taskOverlayModes: Record<string, FootprintOverlayMode>;
    cloudOverlayMap: Record<string, string>;
    filterStartDate?: Date;
    filterEndDate?: Date;
    displayStacItem?: StacItem;
    timelineScrubberVisible?: boolean;
    numTimelineScrubberBuckets: number;
    searchError: boolean;
};

export type FootprintOverlayMode = 'none' | 'clouds' | 'rgb';

const initialState: SearchStateType = {
    resultMap: {},
    fileIdToCollectMap: {},
    collectToFileIdList: {},
    roi: undefined,
    roiName: undefined,
    roiType: undefined,
    isSearching: false,
    results: undefined,
    apiResultsLength: 0,
    platforms: [],
    startDate: null,
    endDate: null,
    timelineMode: 'daily',
    timelineDate: null,
    searchPanel: true,
    isSearchDirty: true,
    cloudCover: 100,
    taskOverlayModes: {},
    cloudOverlayMap: {},
    filterStartDate: undefined,
    filterEndDate: undefined,
    timelineScrubberVisible: false,
    numTimelineScrubberBuckets: 0,
    searchError: false,
};

export default function reducer(state = initialState, action: any) {
    switch (action.type) {
        case SET_ROI: {
            const { geoJson, aoiName, aoiType } = action.payload;
            return {
                ...state,
                roi: geoJson,
                roiName: aoiName,
                // Only set the type if there's a valid geoJson provided.
                // Otherwise clear the type.
                roiType: geoJson?.features.length > 0 ? aoiType : undefined,
            };
        }

        case SET_SEARCH_ERROR: {
            const { searchError } = action.payload;
            return {
                ...state,
                searchError,
            };
        }

        case SET_SEARCHING: {
            const { isSearching } = action.payload;
            return {
                ...state,
                isSearching,
            };
        }

        case UPDATE_SEARCH_RESULT: {
            const { result, retainSceneIds } = action.payload;
            const fileIdToCollectMap: Record<string, string> = {};
            const collectToFileIdList: Record<string, string[]> = {};
            const newCollect: Record<string, string[]> = {};
            const resultMap: Record<string, OSKScene> = {};
            const newResults: OSKScene[] = result ? [...result] : [];

            // Copy the previous result map
            Object.entries(state.resultMap).forEach((e) => (resultMap[e[0]] = e[1]));

            // If we're given a list of taskIds to retain, copy those forward
            Object.entries(state.collectToFileIdList)
                .filter((c) => retainSceneIds.includes(c[0]))
                .forEach((c) => {
                    newCollect[c[0]] = [...c[1]];
                });

            // Copy them forward for results too
            Object.values((state.results as OSKScene[]) ?? [])
                .filter((scene) => retainSceneIds.includes(scene.id))
                .forEach((scene) => {
                    if (!newResults.find((result) => result.id === scene.id)) newResults.push(scene);
                });

            newResults.forEach((result: OSKScene) => {
                const task_id = result.task_id;
                fileIdToCollectMap[result.id] = task_id;
                if (!(task_id in newCollect)) newCollect[task_id] = [];
                newCollect[task_id].push(result.id);
                resultMap[result.id] = result;
            });

            const buckets = bucketByDateNoGaps(result);

            return {
                ...state,
                results: newResults,
                fileIdToCollectMap,
                collectToFileIdList: {
                    ...collectToFileIdList,
                    ...newCollect,
                },
                numTimelineScrubberBuckets: buckets.length,
                resultMap,
            };
        }

        case UPDATE_API_RESULTS_LENGTH: {
            const { length } = action.payload;

            return {
                ...state,
                apiResultsLength: length,
            };
        }

        case INCLUDE_PLATFORM: {
            const { platform }: any = action.payload;
            const nextPlatforms = [...state.platforms];
            if (!nextPlatforms.includes(platform)) {
                nextPlatforms.push(platform);
            }

            return {
                ...state,
                platforms: nextPlatforms,
            };
        }

        case SET_TIMELINE_DATE: {
            const { date }: any = action.payload;
            return {
                ...state,
                timelineDate: date,
            };
        }

        case SET_TIMELINE_MODE: {
            const { mode }: any = action.payload;
            return {
                ...state,
                timelineMode: mode,
            };
        }

        case SET_DATE_RANGE: {
            const { start, end }: any = action.payload;
            return {
                ...state,
                startDate: start,
                endDate: end,
            };
        }

        case SET_CLOUD_COVER_FILTER: {
            const { cloudCover }: any = action.payload;

            return {
                ...state,
                cloudCover,
            };
        }

        case SET_SUN_AZIMUTH_FILTER: {
            const { sunAzimuthRange }: any = action.payload;

            return {
                ...state,
                sunAzimuthRange,
            };
        }

        case SET_SUN_ELEVATION_FILTER: {
            const { sunElevationRange }: any = action.payload;

            return {
                ...state,
                sunElevationRange,
            };
        }

        case SET_OFF_NADIR_FILTER: {
            const { offNadirRange }: any = action.payload;

            return {
                ...state,
                offNadirRange,
            };
        }

        case SET_VIEW_AZIMUTH_FILTER: {
            const { viewAzimuthRange }: any = action.payload;

            return {
                ...state,
                viewAzimuthRange,
            };
        }

        case SET_IMAGING_MODE_FILTER: {
            const { imagingModes }: any = action.payload;

            return {
                ...state,
                imagingModes,
            };
        }

        case SET_PURCHASED_ITEMS_FILTER: {
            const { purchasedItemsOnly }: any = action.payload;

            return {
                ...state,
                purchasedItemsOnly,
            };
        }

        case EXCLUDE_PLATFORM: {
            const { platform } = action.payload;
            const nextPlatforms = [...state.platforms];
            if (nextPlatforms.includes(platform)) {
                nextPlatforms.splice(nextPlatforms.indexOf(platform), 1);
            }
            return {
                ...state,
                platforms: nextPlatforms,
            };
        }

        case TOGGLE_SEARCH_PANEL: {
            const { searchPanel } = state;
            return { ...state, searchPanel: !searchPanel };
        }

        case SET_IS_SEARCH_DIRTY: {
            const { isSearchDirty } = action.payload;
            return { ...state, isSearchDirty };
        }

        case SET_OVERLAY_MODE_FOR_TASK: {
            const { id, mode } = action.payload;

            // Copy the previous overlay map
            const taskOverlayModes: Record<string, FootprintOverlayMode> = {};
            Object.entries(state.taskOverlayModes).forEach((e) => (taskOverlayModes[e[0]] = e[1]));

            taskOverlayModes[id as string] = mode;

            return { ...state, taskOverlayModes };
        }

        case CLEAR_OVERLAY_MODES: {
            return { ...state, taskOverlayModes: {} };
        }

        case ADD_CLOUD_OVERLAY_MAP: {
            const { file_id, cloud_overlay } = action.payload;

            // Copy the previous overlay map
            const newOverlayMap: Record<string, string> = {};
            Object.entries(state.cloudOverlayMap).forEach((e) => (newOverlayMap[e[0]] = e[1]));

            newOverlayMap[file_id] = cloud_overlay;

            return {
                ...state,
                cloudOverlayMap: newOverlayMap,
            };
        }

        case CLEAR_CLOUD_OVERLAY_MAP: {
            return {
                ...state,
                cloudOverlayMap: {},
            };
        }

        case SET_FILTER_DATES: {
            const { start, end } = action.payload;
            return {
                ...state,
                filterStartDate: start,
                filterEndDate: end,
            };
        }

        case SET_DISPLAY_STAC_ITEM: {
            const { displayStacItem } = action.payload;

            return {
                ...state,
                displayStacItem,
            };
        }

        case SET_TIMELINE_SCRUBBER_VISIBLE: {
            const { timelineScrubberVisible } = action.payload;
            return {
                ...state,
                timelineScrubberVisible,
            };
        }

        default:
            return { ...state };
    }
}

/* Selectors */
export const hasRoI = (state: RootState) => {
    return state.data.search.roi !== undefined && (state.data.search.roi as OSKGeoJson).toCoordinates().length > 0;
};

export const canSearch = (state: RootState) => {
    return (
        state.data.search.endDate !== null ||
        state.data.search.startDate !== null ||
        hasRoI(state) ||
        state.data.search.platforms.length > 0
    );
};

/* Selectors */
export const selectedSensors = (state: RootState) => {
    return filter(state.osk.sensors, (sensor: Sensor) => state.data.search.platforms.includes(sensor.osk_id));
};

function filterFilesByCaptureDateRange(
    files: OSKScene[],
    filterStartDate: Date | undefined,
    filterEndDate: Date | undefined,
) {
    return files.filter((file) => {
        if (file.datetime && filterStartDate && filterEndDate) {
            const date = new Date(file.datetime);
            return date.getTime() >= filterStartDate.getTime() && date.getTime() <= filterEndDate?.getTime();
        } else {
            return true;
        }
    });
}

export const getGroupedFiles = createSelector(
    (state: RootState) => state.data.search.results,
    (state: RootState) => state.data.search.startDate,
    (state: RootState) => state.data.search.endDate,
    (state: RootState) => state.data.search.platforms, // selected sensors
    (scenes: OSKScene[] | undefined, startDate: Date, endDate: Date, platforms: number[]) => {
        return filterFilesByCaptureDateRange(scenes ?? [], startDate, endDate)
            .filter((scene) => platforms.includes(parseInt(scene.sensor_serial))) // Filter by selected sensor
            .reduce((groupedFiles: Record<string, OSKScene[]>, file: OSKScene) => {
                if (groupedFiles.hasOwnProperty(file.task_id)) {
                    groupedFiles[file.task_id] = groupedFiles[file.task_id].concat(file);
                } else {
                    groupedFiles[file.task_id] = [file];
                }
                return groupedFiles;
            }, {});
    },
);

export const allTaskOverlayModes = (state: RootState) => {
    return Object.values(state.data.search.taskOverlayModes);
};

export const selectResults = createSelector([(state: RootState) => state.data.search.results], (results) => {
    return results;
});
